import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { flushSync } from 'react-dom'

const ThemeContext = createContext()

const THEME_KEY = 'xtractai-theme'
const LEGACY_THEME_KEY = 'lcp-theme'
const DARK_STYLE_KEY = 'xtractai-dark-style'

const MODES = ['light', 'dark']
export const DARK_STYLES = ['enhanced', 'medium', 'gentle']
const DEFAULT_DARK_STYLE = 'medium'

function readStorage(key, allowed) {
  try {
    const value = localStorage.getItem(key)
    return allowed.includes(value) ? value : null
  } catch {
    // Private mode / blocked storage — fall through to defaults.
    return null
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Non-fatal: the theme still applies for this session.
  }
}

/**
 * Resolve the stored mode, migrating the legacy 'lcp-theme' key. There is no
 * ongoing "system" mode — with nothing stored yet (a fresh install, or an
 * older visit that had saved the now-removed 'system' option and so no
 * longer matches `MODES`), the OS preference is read ONCE to pick a sensible
 * starting point, then treated as a normal, explicit choice from then on.
 */
function getStoredMode() {
  const current = readStorage(THEME_KEY, MODES)
  if (current) return current

  const legacy = readStorage(LEGACY_THEME_KEY, ['dark', 'light'])
  if (legacy) {
    writeStorage(THEME_KEY, legacy)
    return legacy
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

// Shared by every appearance change (light/dark AND intensity), so a switch
// always reads as "one consistent motion", never a different flourish
// depending on which control was used.
const REVEAL_DURATION = 500
const REVEAL_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'

/**
 * Animate a root-level appearance change as a circular reveal growing from
 * `originEl` — the Appearance icon in the header, for every selection (mode
 * buttons, intensity swatches, arrow-key navigation alike) — until the new
 * look has covered the page. Uses the View Transitions API, which gives a
 * real crossfade between the actual before/after page content rather than a
 * flat colour wash.
 *
 * Falls back to an instant flip when the API is unavailable (Firefox, and
 * Safari before 18 — support is Chromium-only otherwise) or the user has
 * asked for reduced motion — running a full-viewport reveal under
 * `prefers-reduced-motion: reduce` is an accessibility failure.
 */
function withThemeTransition(apply, originEl) {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  if (typeof document.startViewTransition !== 'function' || prefersReducedMotion) {
    apply()
    return
  }

  // flushSync forces React to commit synchronously inside the callback, so the
  // API captures the *new* state rather than the pre-update paint.
  const transition = document.startViewTransition(() => flushSync(apply))

  transition.ready
    .then(() => {
      const rect = originEl?.getBoundingClientRect?.()
      const x = rect ? rect.left + rect.width / 2 : window.innerWidth / 2
      const y = rect ? rect.top + rect.height / 2 : window.innerHeight / 2

      // Radius that reaches the furthest viewport corner from the origin, so
      // the circle has fully covered every corner of the page by the end.
      const radius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y)
      )

      document.documentElement.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
        { duration: REVEAL_DURATION, easing: REVEAL_EASING, pseudoElement: '::view-transition-new(root)' }
      )
    })
    .catch(() => {
      // A superseded transition rejects; the theme has still been applied.
    })
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(getStoredMode)
  const [darkStyle, setDarkStyleState] = useState(
    () => readStorage(DARK_STYLE_KEY, DARK_STYLES) || DEFAULT_DARK_STYLE
  )

  const isDark = mode === 'dark'

  // Suppress the colour cross-fade on the very first paint, otherwise the app
  // visibly animates from the default palette into the stored theme on load.
  const isFirstApply = useRef(true)

  useEffect(() => {
    const root = document.documentElement

    // A short-lived class rather than a permanent global `transition` rule, so
    // intensity changes cross-fade without paying the paint cost on every hover.
    let timer
    if (!isFirstApply.current) {
      root.classList.add('theme-transition')
      timer = window.setTimeout(() => root.classList.remove('theme-transition'), 220)
    }
    isFirstApply.current = false

    root.classList.toggle('dark', isDark)
    root.dataset.darkStyle = darkStyle

    writeStorage(THEME_KEY, mode)
    writeStorage(LEGACY_THEME_KEY, mode)
    writeStorage(DARK_STYLE_KEY, darkStyle)

    return () => window.clearTimeout(timer)
  }, [isDark, mode, darkStyle])

  const setMode = useCallback((next, originEl) => {
    if (!MODES.includes(next) || next === mode) return // reselecting has nothing to reveal
    withThemeTransition(() => setModeState(next), originEl)
  }, [mode])

  const setDarkStyle = useCallback((next, originEl) => {
    if (!DARK_STYLES.includes(next) || next === darkStyle) return
    if (!isDark) {
      // The swatches only affect anything once dark mode is active (see the
      // popover, which hides this section entirely in light mode) — nothing
      // on screen would actually change yet, so there is nothing to reveal.
      setDarkStyleState(next)
      return
    }
    // Same circular-reveal treatment as the light/dark switch, growing from
    // the Appearance icon — a palette change should feel like one consistent
    // motion language with the mode switch, not a plain colour snap.
    withThemeTransition(() => setDarkStyleState(next), originEl)
  }, [darkStyle, isDark])

  // Kept on the context as a convenience escape hatch for a plain light/dark
  // flip outside the Appearance popover (e.g. a future keyboard shortcut).
  const toggleTheme = useCallback((originEl) => {
    withThemeTransition(() => setModeState(isDark ? 'light' : 'dark'), originEl)
  }, [isDark])

  const theme = isDark ? 'dark' : 'light'

  return (
    <ThemeContext.Provider
      value={{ theme, isDark, toggleTheme, mode, setMode, darkStyle, setDarkStyle }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
